import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { queue } from "@/components/Toast";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { useRefreshAiBudget } from "@/hooks/useAiBudget";
import { reconcileSignals } from "@/app/routes/dashboard/market-signals/api/signalsCache";
import { brandMemoriesKey } from "../../settings/api/useBrandMemories";
import { USER_MEMORIES_ROOT } from "../../settings/api/useUserMemories";
import type {
  ActiveTurn,
  ChatMessage,
  ChatModel,
  PersistedChatMessage,
  ReasoningEffort,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { WorkshopQuickCreateRef, WorkshopSession } from "@/types/workshop.type";
import {
  buildAssistantMessageMetadata,
  parseUsageFromSse,
} from "../../components/chatMessageMetadata";
import {
  createStreamingTurnBuilder,
  type StreamingBlock,
} from "../../components/streamingTurnBlocks";
import { newlyAttachedDocumentIds } from "../../components/attachedVideos";
import {
  isTurnStale,
  persistedToChatMessage,
  splitSessionMessages,
  useResumableChatTurn,
} from "../../components/resumeChatTurn";
import {
  newLiveSend,
  useStopChatTurn,
  type LiveSend,
} from "../../components/stopChatTurn";
import {
  workshopArtifactKey,
  workshopArtifactNotesRootKey,
  workshopArtifactsKey,
  workshopContextKey,
  workshopDeploymentsKey,
  workshopFilesKey,
  workshopIdeaKey,
  workshopIdeasKey,
  workshopMediaKey,
  workshopProjectKey,
  workshopProjectsKey,
  workshopSessionsKey,
} from "./workshopKeys";
import { isUnreachable } from "../workshopThread";
import {
  countUserRows,
  keepKeys,
  mergeNewestPage,
  oldestStored,
  prependOlder,
  THREAD_PAGE,
  threadPageUrl,
  type ThreadPageResponse,
} from "./threadPages";
import { QUICK_CREATE_UNAVAILABLE, pickId, quickCreatePayload } from "../quickCreate";

/** Tool names whose completion means the project's artifacts changed. */
const ARTIFACT_MUTATING_TOOLS = new Set([
  "CreateArtifact",
  "UpdateArtifact",
  "EditArtifact",
]);

/** Tool names whose completion means an artifact note changed. */
const NOTE_MUTATING_TOOLS = new Set(["ResolveArtifactNote"]);

/** Tool names whose completion means a project concept idea (or its brief) changed. */
const IDEA_MUTATING_TOOLS = new Set([
  "SaveConceptIdea",
  "UpdateConceptIdea",
  "SaveConceptBrief",
  "UpdateConceptBrief",
]);

/** Tool names whose completion means the project's context items changed. */
const CONTEXT_MUTATING_TOOLS = new Set(["AddContextItem", "RemoveContextItem"]);

/** Tool names whose completion means the project's deployments changed. */
const DEPLOYMENT_MUTATING_TOOLS = new Set(["DeployWorkshopAgent", "MessageAgent"]);

/** Tool names whose completion means the project itself (name, description) changed. */
const PROJECT_MUTATING_TOOLS = new Set(["UpdateProject"]);

/**
 * Tool names whose completion means the PROJECT's media changed. An image the
 * agent makes here stays in the project; the brand library only gets it if the
 * user copies it there.
 */
const MEDIA_MUTATING_TOOLS = new Set(["GenerateImage"]);

/** Tool names whose completion means the project's files (Sources) changed. */
const FILE_MUTATING_TOOLS = new Set(["AttachVideoFromLink"]);

/** Tool names whose completion means the session's active skills changed. */
const SKILL_MUTATING_TOOLS = new Set(["LoadSkill", "ExitSkill"]);

/** Same cross-surface invalidations the Brand Universe chat does. */
const SIGNAL_MUTATING_TOOLS = new Set(["SetMarketSignal"]);
const MEMORY_MUTATING_TOOLS = new Set(["SaveMemory"]);
const PERSONAL_MEMORY_MUTATING_TOOLS = new Set(["SavePersonalMemory"]);

function toolResultId(result: unknown, key: string): string | null {
  if (!result || typeof result !== "object" || !(key in result)) return null;
  return String((result as Record<string, unknown>)[key] ?? "") || null;
}

class ChatStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatStreamError";
  }
}

/**
 * A message that got no answer at all and, as far as a read of the thread can
 * tell, never reached Mohtawa. It is taken back out of the transcript and waits
 * above its thread's composer; nothing resends it on its own, and a later send
 * never drops it. `sessionId` is its thread (null: a thread not created yet).
 */
export type WorkshopUnsentMessage = {
  text: string;
  sessionId: string | null;
  /**
   * The thread was read after the failure and did not have it. False when that
   * read failed too: Try again reads the thread before sending again.
   */
  checked: boolean;
  /** Messages of the person's in the thread before this send: tells it apart from an older twin. */
  userRowsBefore: number;
  /**
   * Each failed send that makes up `text`, in order. A second failure in the
   * same thread joins the first, and Try again checks each part on its own, so
   * a part that reached the thread after all is never sent twice.
   */
  parts?: string[];
};

/** The sends a waiting message is made of. */
export function unsentParts(entry: WorkshopUnsentMessage): string[] {
  return entry.parts && entry.parts.length > 0 ? entry.parts : [entry.text];
}

/** One waiting message per thread; a thread not created yet waits under "new". */
export const unsentKey = (sessionId: string | null) => sessionId ?? "new";

/**
 * A send waits this long for the next one before it leaves, and never longer
 * than the cap in all: a thought typed as two quick sends is read as one turn,
 * instead of Creo answering the first half and then the second (user report,
 * 12 Oct).
 */
const GATHER_MS = 800;
const GATHER_MAX_MS = 3_000;

/** The send that is still gathering: its words so far, its bubble, its timer. */
type Gathering = { texts: string[]; messageId: string; arm: () => void };

export type WorkshopSendMessageOptions = {
  /** Start a brand-new session for this turn, ignoring the currently open one. */
  newSession?: boolean;
  /** Override the rail picker for this turn (draft bootstrap). */
  modelId?: string;
  reasoningEffort?: ReasoningEffort;
  /** The skills and agents this message starts; the words may then be empty. */
  quickCreates?: WorkshopQuickCreateRef[];
  /**
   * The API refused a pick (it went): the words and the pills go back to the
   * composer. `refused` is that pick's id (pickId), or "*" when unnamed.
   */
  onQuickCreateRefused?: (refused: string) => void;
};

type RefusedBody = { code?: unknown; kind?: unknown; key?: unknown };

/** The body of a refused send. A streamed request's body is still a stream. */
async function refusedBody(error: unknown): Promise<RefusedBody | null> {
  if (!isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (data instanceof ReadableStream) {
    return (await new Response(data).json().catch(() => null)) as RefusedBody | null;
  }
  if (data && typeof data === "object") return data as RefusedBody;
  return null;
}

/**
 * The workshop chat — a clone of useBrandUniverseChat pointed at the
 * /api/workshop endpoints (identical SSE frames; see chat-abort-plan and
 * realtime-polling-plan for the shared turn machinery it reuses).
 *
 * Model/effort selection is session-persisted server-side (PUT /sessions/:id),
 * not in chatModelStore — the panel owns persistence and seeds the picker from
 * the session row via seedSelection.
 */
export function useWorkshopChat(
  brandId: string,
  projectId: string,
  sessionId: string | null,
  callbacks?: {
    onSessionCreated?: (session: WorkshopSession) => void;
    onSessionsChanged?: () => void;
    /** An artifact write landed; artifactId when the tool result carried one. */
    onArtifactWritten?: (artifactId: string | null) => void;
    /** A concept idea was saved or updated; id/title when the tool result carried them. */
    onIdeaWritten?: (ideaId: string | null, title: string | null) => void;
    /** A context item was pinned; id when the tool result carried one. */
    onContextWritten?: (contextItemId: string | null) => void;
    /** An agent was deployed; id when the tool result carried one. */
    onDeploymentWritten?: (deploymentId: string | null) => void;
    /** An image was generated into the project's Media; id when the result carried one. */
    onMediaWritten?: (mediaId: string | null) => void;
    /** A new file joined the project's Sources (a video attached from a link). */
    onFileWritten?: (documentId: string) => void;
  },
) {
  const {
    onSessionCreated,
    onSessionsChanged,
    onArtifactWritten,
    onIdeaWritten,
    onContextWritten,
    onDeploymentWritten,
    onMediaWritten,
    onFileWritten,
  } = callbacks ?? {};
  const refreshAiBudget = useRefreshAiBudget();
  const queryClient = useQueryClient();
  const currentSessionKey = sessionId ? `${projectId}:${sessionId}` : null;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadedSessionKey, setLoadedSessionKey] = useState<string | null>(null);
  const [streamingBlocks, setStreamingBlocks] = useState<StreamingBlock[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // Bumped when a live stream dies before its terminal event — hands the turn
  // to the resume loop, which follows the still-generating reply in place.
  const [streamRecoveryKey, setStreamRecoveryKey] = useState(0);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [unsentByThread, setUnsentByThread] = useState<Record<string, WorkshopUnsentMessage>>({});
  const unsentRef = useRef(unsentByThread);
  // The transcript as this tab last had it, for counting what a send came after.
  const messagesRef = useRef<ChatMessage[]>([]);
  const isStreamingRef = useRef(false);
  isStreamingRef.current = isStreaming;
  const [selectedModel, setSelectedModelState] = useState<ChatModel | null>(null);
  const [selectedReasoningEffort, setSelectedReasoningEffortState] =
    useState<ReasoningEffort>("high");
  // Which session key ("draft" | session id) the picker was last seeded for.
  const [seededKey, setSeededKey] = useState<string | null>(null);
  // Real context size (tokens) after the last reply, from provider usage.
  // null = no real usage yet — the meter stays hidden until one lands.
  const [contextTokens, setContextTokens] = useState<number | null>(null);
  // Input + output of the latest model round on the live private turn.
  const [liveTokens, setLiveTokens] = useState<number | null>(null);
  // Paging (threadPages.ts): older messages exist before the oldest one loaded.
  const [hasOlder, setHasOlder] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  /**
   * The person's messages in the pages not loaded: with the loaded ones, every
   * message of theirs in the thread, which is what a send is counted against
   * (userRowsBefore) when it has to be looked for.
   */
  const unloadedUserRows = useRef(0);
  const [userRowOffset, setUserRowOffset] = useState(0);
  const setUnloadedUserRows = useCallback((count: number) => {
    unloadedUserRows.current = Math.max(0, count);
    setUserRowOffset(unloadedUserRows.current);
  }, []);
  const pagedSessionRef = useRef(sessionId);
  pagedSessionRef.current = sessionId;

  const setSelectedModel = useCallback((model: ChatModel) => {
    setSelectedModelState(model);
  }, []);

  const setSelectedReasoningEffort = useCallback((effort: ReasoningEffort) => {
    setSelectedReasoningEffortState(effort);
  }, []);

  /**
   * Seeds the picker from the session row without triggering persistence.
   * Passing seedKey also records which session the seed was for.
   */
  const seedSelection = useCallback(
    (model: ChatModel, effort: ReasoningEffort, seedKey?: string) => {
      setSelectedModelState(model);
      setSelectedReasoningEffortState(effort);
      if (seedKey !== undefined) setSeededKey(seedKey);
    },
    [],
  );

  /** The draft seed carries over to a session created mid-turn. */
  const markSeeded = useCallback((key: string) => setSeededKey(key), []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    unsentRef.current = unsentByThread;
  }, [unsentByThread]);

  /** Hold a message that did not go. A second one in the same thread joins the first. */
  const holdUnsent = useCallback((entry: WorkshopUnsentMessage) => {
    setUnsentByThread((current) => {
      const key = unsentKey(entry.sessionId);
      const waiting = current[key];
      if (!waiting) return { ...current, [key]: { ...entry, parts: unsentParts(entry) } };
      const parts = [...unsentParts(waiting), ...unsentParts(entry)];
      return {
        ...current,
        [key]: {
          ...waiting,
          parts,
          text: parts.join("\n\n"),
          checked: waiting.checked && entry.checked,
        },
      };
    });
  }, []);

  /** Take a thread's waiting message out (to send it again, edit it or discard it). */
  const takeUnsent = useCallback((sessionId: string | null): WorkshopUnsentMessage | null => {
    const key = unsentKey(sessionId);
    const entry = unsentRef.current[key] ?? null;
    if (entry) {
      // Gone at once, so a second press before the next render takes nothing.
      const rest = { ...unsentRef.current };
      delete rest[key];
      unsentRef.current = rest;
      setUnsentByThread((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
    return entry;
  }, []);

  /**
   * Whether a send that got no answer reached the thread after all: the newest
   * message of the person's is that text and came after the send. The server
   * stores the message before it starts the reply, so a drop in between
   * reaches this tab as no answer. Null when the thread could not be read.
   */
  const sendLanded = useCallback(
    async (
      sessionId: string,
      text: string,
      userRowsBefore: number,
    ): Promise<{ landed: boolean; replying: boolean } | null> => {
      try {
        const [thread, active] = await Promise.all([
          axiosInstance.get<{ messages: PersistedChatMessage[] }>(`/workshop/sessions/${sessionId}`),
          axiosInstance.get<{ turn: ActiveTurn | null }>(`/workshop/sessions/${sessionId}/active-turn`),
        ]);
        const mine = thread.data.messages.filter((m) => m.role === "user");
        const newest = mine[mine.length - 1];
        const turn = active.data.turn;
        return {
          landed: mine.length > userRowsBefore && newest?.content.trim() === text.trim(),
          replying: Boolean(turn && turn.status === "streaming" && !isTurnStale(turn.updatedAt)),
        };
      } catch {
        return null;
      }
    },
    [],
  );

  /**
   * What Try again still has to send. A waiting message that was checked after
   * its failure and that is a single send goes as it is. Otherwise the thread is
   * read first, and every part already among the person's newer messages is
   * dropped (its reply is followed instead), so nothing is sent twice. Null when
   * the thread cannot be read: then nothing is sent, because a blind resend is
   * exactly how a message ends up in the thread twice.
   */
  const unsentRemainder = useCallback(
    async (entry: WorkshopUnsentMessage): Promise<{ rest: string[]; userRows: number } | null> => {
      const parts = unsentParts(entry);
      if (!entry.sessionId || (entry.checked && parts.length === 1)) {
        return { rest: parts, userRows: entry.userRowsBefore };
      }
      try {
        const thread = await axiosInstance.get<{ messages: PersistedChatMessage[] }>(
          `/workshop/sessions/${entry.sessionId}`,
        );
        const mine = thread.data.messages.filter((m) => m.role === "user");
        const newer = mine.slice(entry.userRowsBefore).map((m) => m.content.trim());
        const rest: string[] = [];
        for (const part of parts) {
          const at = newer.indexOf(part.trim());
          if (at === -1) rest.push(part);
          else newer.splice(at, 1);
        }
        if (rest.length < parts.length) setStreamRecoveryKey((k) => k + 1);
        return { rest, userRows: mine.length };
      } catch {
        return null;
      }
    },
    [],
  );

  const invalidateForTool = useCallback(
    (toolName: string, result: unknown) => {
      if (ARTIFACT_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopArtifactsKey(projectId),
        });
        const artifactId = toolResultId(result, "artifactId");
        if (artifactId) {
          void queryClient.invalidateQueries({
            queryKey: workshopArtifactKey(artifactId),
          });
        }
        onArtifactWritten?.(artifactId);
      }
      if (NOTE_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopArtifactNotesRootKey(projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: workshopArtifactsKey(projectId),
        });
      }
      if (IDEA_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopIdeasKey(projectId),
        });
        const ideaId = toolResultId(result, "ideaId");
        const ideaTitle = toolResultId(result, "title");
        if (ideaId) {
          void queryClient.invalidateQueries({
            queryKey: workshopIdeaKey(ideaId),
          });
        }
        onIdeaWritten?.(ideaId, ideaTitle);
      }
      if (CONTEXT_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopContextKey(projectId),
        });
        if (toolName === "AddContextItem") {
          onContextWritten?.(toolResultId(result, "contextItemId"));
        }
      }
      if (DEPLOYMENT_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopDeploymentsKey(projectId),
        });
        onDeploymentWritten?.(toolResultId(result, "deploymentId"));
      }
      if (PROJECT_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({ queryKey: workshopProjectsKey(brandId) });
        void queryClient.invalidateQueries({ queryKey: workshopProjectKey(projectId) });
      }
      if (MEDIA_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({ queryKey: workshopMediaKey(projectId) });
        onMediaWritten?.(toolResultId(result, "mediaId"));
      }
      if (FILE_MUTATING_TOOLS.has(toolName)) {
        // The prefix covers both the Sources list and this thread's strip.
        void queryClient.invalidateQueries({ queryKey: workshopFilesKey(projectId) });
        for (const documentId of newlyAttachedDocumentIds({
          toolName,
          toolCallId: "",
          input: null,
          result,
          startedAt: "",
          endedAt: "",
        })) {
          onFileWritten?.(documentId);
        }
      }
      if (SKILL_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({
          queryKey: workshopSessionsKey(projectId),
        });
      }
      if (SIGNAL_MUTATING_TOOLS.has(toolName)) reconcileSignals(queryClient, brandId);
      if (MEMORY_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({ queryKey: brandMemoriesKey(brandId) });
      }
      if (PERSONAL_MEMORY_MUTATING_TOOLS.has(toolName)) {
        void queryClient.invalidateQueries({ queryKey: USER_MEMORIES_ROOT });
      }
    },
    [queryClient, projectId, brandId, onArtifactWritten, onIdeaWritten, onContextWritten, onDeploymentWritten, onMediaWritten, onFileWritten],
  );

  useEffect(() => {
    let cancelled = false;
    if (!sessionId || !currentSessionKey) {
      setMessages([]);
      setLoadedSessionKey(null);
      setContextTokens(null);
      setHasOlder(false);
      setUnloadedUserRows(0);
      return;
    }
    if (isStreaming) return;
    // Only fetch when the session actually changed — ending a stream must not
    // refetch and replace the just-streamed optimistic messages.
    if (currentSessionKey === loadedSessionKey) return;

    setMessages([]);
    setContextTokens(null);
    setHasOlder(false);
    setUnloadedUserRows(0);

    void (async () => {
      try {
        // The newest page; older ones are read as the thread is scrolled up.
        const res = await axiosInstance.get<ThreadPageResponse>(threadPageUrl(sessionId));
        if (cancelled) return;
        // A "streaming" tail row is kept out of history; useResumableChatTurn
        // renders it live and follows it to completion.
        const loaded = splitSessionMessages(res.data.messages).messages;
        setMessages(loaded);
        setHasOlder(Boolean(res.data.hasMore));
        setUnloadedUserRows(
          (res.data.userRows ?? 0) - countUserRows(res.data.messages.map(persistedToChatMessage)),
        );
        const lastUsage = [...res.data.messages]
          .reverse()
          .find((m) => m.metadata?.usage)?.metadata?.usage;
        setContextTokens(lastUsage?.contextTokens ?? null);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load workshop chat session:", error);
          setMessages([]);
          setContextTokens(null);
        }
      } finally {
        if (!cancelled) setLoadedSessionKey(currentSessionKey);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, currentSessionKey, sessionId, isStreaming, loadedSessionKey, setUnloadedUserRows]);

  /**
   * A finished turn's transcript, read as the newest page: it replaces what
   * this tab wrote while following the reply, and keeps the older pages read on
   * the way up.
   */
  const applyNewestPage = useCallback((data: ThreadPageResponse) => {
    const current = messagesRef.current;
    const page = keepKeys(data.messages.map(persistedToChatMessage), current);
    const { messages: merged, keptOlder } = mergeNewestPage(current, page, Boolean(data.hasMore));
    setMessages(merged);
    if (!keptOlder) setHasOlder(Boolean(data.hasMore));
    setUnloadedUserRows((data.userRows ?? 0) - countUserRows(merged));
  }, [setUnloadedUserRows]);

  /** The page before the oldest message loaded (the thread was scrolled up). */
  const loadOlder = useCallback(async (): Promise<boolean> => {
    const id = pagedSessionRef.current;
    const oldest = oldestStored(messagesRef.current);
    if (!id || !oldest || loadingOlderRef.current) return false;
    loadingOlderRef.current = true;
    setIsLoadingOlder(true);
    try {
      const res = await axiosInstance.get<ThreadPageResponse>(threadPageUrl(id, oldest.id));
      if (pagedSessionRef.current !== id) return false;
      const older = res.data.messages.map(persistedToChatMessage);
      setMessages((current) => prependOlder(current, older));
      setHasOlder(Boolean(res.data.hasMore));
      setUnloadedUserRows(unloadedUserRows.current - countUserRows(older));
      return true;
    } catch (error) {
      console.error("Failed to load earlier messages:", error);
      return false;
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [setUnloadedUserRows]);

  /**
   * Back at the newest end: let the pages read on the way up go again. A
   * private thread shows what it sends before the server has it (local-…),
   * so it trims by reading its newest page afresh: the window comes back to a
   * page, with the server's rows (and their ids, which the next older page is
   * read from) under the keys already on screen. Not while a reply streams.
   */
  const trimOlder = useCallback(async () => {
    const id = pagedSessionRef.current;
    if (!id || loadingOlderRef.current || isStreamingRef.current) return;
    if (messagesRef.current.length <= THREAD_PAGE) return;
    loadingOlderRef.current = true;
    try {
      const res = await axiosInstance.get<ThreadPageResponse>(threadPageUrl(id));
      // A send started meanwhile: its bubble is not on the server yet.
      if (pagedSessionRef.current !== id || isStreamingRef.current) return;
      const page = keepKeys(
        splitSessionMessages(res.data.messages).messages,
        messagesRef.current,
      );
      setMessages(page);
      setHasOlder(Boolean(res.data.hasMore));
      setUnloadedUserRows((res.data.userRows ?? 0) - countUserRows(page));
    } catch (error) {
      console.error("Failed to trim the thread:", error);
    } finally {
      loadingOlderRef.current = false;
    }
  }, [setUnloadedUserRows]);

  useResumableChatTurn({
    sessionKey: currentSessionKey,
    sessionUrl: sessionId ? `/workshop/sessions/${sessionId}` : null,
    transcriptUrl: sessionId ? threadPageUrl(sessionId) : null,
    isStreamingRef,
    setters: {
      setIsStreaming,
      setStreamingBlocks,
      setMessages,
      setContextTokens,
      applyTranscript: applyNewestPage,
    },
    onSettled: () => {
      setLiveTokens(null);
      refreshAiBudget();
      onSessionsChanged?.();
      // A resumed turn may have written artifacts/ideas/context/deployments/media
      // while this tab wasn't parsing tool_end frames — refetch them all.
      void queryClient.invalidateQueries({ queryKey: workshopArtifactsKey(projectId) });
      void queryClient.invalidateQueries({ queryKey: workshopIdeasKey(projectId) });
      void queryClient.invalidateQueries({ queryKey: workshopContextKey(projectId) });
      void queryClient.invalidateQueries({ queryKey: workshopDeploymentsKey(projectId) });
      void queryClient.invalidateQueries({ queryKey: workshopMediaKey(projectId) });
    },
    recoveryKey: streamRecoveryKey,
  });

  const liveSendRef = useRef<LiveSend | null>(null);
  const { stopStreaming, isStopping } = useStopChatTurn({
    sessionId,
    abortUrlFor: (id) => `/workshop/sessions/${id}/abort`,
    liveSendRef,
    isStreaming,
    onStalled: () => setStreamRecoveryKey((k) => k + 1),
  });

  const gatherRef = useRef<Gathering | null>(null);

  /**
   * Adds a message to the send that has not left yet (inside its gather
   * window): its words join the same bubble as a paragraph of their own and the
   * window starts again. False when nothing is gathering.
   */
  const joinSend = useCallback((content: string): boolean => {
    const gathering = gatherRef.current;
    const text = content.trim();
    if (!gathering || !text) return false;
    gathering.texts.push(text);
    const joined = gathering.texts.join("\n\n");
    setMessages((prev) => prev.map((m) => (m.id === gathering.messageId ? { ...m, content: joined } : m)));
    gathering.arm();
    return true;
  }, []);

  const sendMessage = useCallback(
    async (content: string, opts?: WorkshopSendMessageOptions) => {
      let trimmed = content.trim();
      const quickCreates = opts?.quickCreates ?? [];
      if ((!trimmed && quickCreates.length === 0) || isStreaming) return;
      const modelId = opts?.modelId ?? selectedModel?.id ?? null;
      const effort = opts?.reasoningEffort ?? selectedReasoningEffort;
      const modelSupportsEffort =
        opts?.modelId != null || Boolean(selectedModel?.reasoningOptions?.length);

      setIsStreaming(true);
      setStreamingBlocks([]);
      setLiveTokens(null);
      setStreamError(null);
      // What this send comes after, to tell it apart from an older twin if it
      // has to be looked for in the thread: every message of theirs in it,
      // the pages not loaded included.
      const userRowsBefore = opts?.newSession
        ? 0
        : unloadedUserRows.current + countUserRows(messagesRef.current);

      let activeSessionId = opts?.newSession ? null : sessionId;
      const turn = createStreamingTurnBuilder();
      let streamStarted = false;
      let handedOffToRecovery = false;
      let userMessageId: string | null = null;
      const send = newLiveSend(activeSessionId);
      liveSendRef.current = send;

      try {
        if (!activeSessionId) {
          const res = await axiosInstance.post<{ session: WorkshopSession }>(
            `/workshop/projects/${projectId}/sessions`,
          );
          activeSessionId = res.data.session.id;
          // A just-created session has no history — mark it loaded now, or the
          // load effect wipes the streamed messages and shows the initial
          // loader the moment isStreaming drops.
          setLoadedSessionKey(`${projectId}:${activeSessionId}`);
          onSessionCreated?.(res.data.session);
          send.sessionId = activeSessionId;
        }
        // Stopped before anything was sent: there is nothing to stop.
        if (send.stopped) return;

        const userMessage: ChatMessage = {
          id: `local-${Date.now()}`,
          workflowRunId: "",
          role: "user",
          content: trimmed,
          metadata: quickCreates.length > 0 ? { quickCreates } : null,
          createdAt: new Date().toISOString(),
        };
        userMessageId = userMessage.id;
        setMessages((prev) => [...prev, userMessage]);
        // The gather window (GATHER_MS): what is sent meanwhile joins this turn
        // through joinSend. A Quick Create goes as it is.
        if (quickCreates.length === 0) {
          const startedAt = Date.now();
          const gathering: Gathering = { texts: [trimmed], messageId: userMessage.id, arm: () => {} };
          await new Promise<void>((resolve) => {
            let timer: ReturnType<typeof setTimeout> | undefined;
            gathering.arm = () => {
              clearTimeout(timer);
              timer = setTimeout(resolve, Math.max(0, Math.min(GATHER_MS, startedAt + GATHER_MAX_MS - Date.now())));
            };
            gatherRef.current = gathering;
            gathering.arm();
          });
          if (gatherRef.current === gathering) gatherRef.current = null;
          trimmed = gathering.texts.join("\n\n");
        }
        // The server's row is created as the turn starts, so the reply sorts
        // right under its question — not below whatever the turn added meanwhile.
        const replyCreatedAt = new Date(Date.parse(userMessage.createdAt) + 1).toISOString();

        const response = await axiosInstance.post<ReadableStream<Uint8Array>>(
          `/workshop/sessions/${activeSessionId}/chat`,
          {
            message: trimmed,
            ...(modelId && modelId !== "auto" ? { model: modelId } : {}),
            ...(modelSupportsEffort ? { reasoningEffort: effort } : {}),
            turnId: send.id,
            ...(quickCreates.length > 0 ? { quickCreates: quickCreatePayload(quickCreates) } : {}),
          },
          {
            responseType: "stream",
            adapter: "fetch",
          },
        );

        // Title is derived server-side as soon as the message is accepted.
        onSessionsChanged?.();

        const stream = response.data;
        if (
          !stream ||
          typeof (stream as ReadableStream).getReader !== "function"
        ) {
          throw new Error("No response body");
        }

        streamStarted = true;
        const decoder = new TextDecoder();
        const reader = (stream as ReadableStream<Uint8Array>).getReader();
        let fullText = "";
        // Every thinking chunk, joined exactly as the server stores them, so the
        // offsets on each tool event slice the same text after a reload.
        let thinkingRaw = "";
        const toolEvents: StreamingToolEvent[] = [];
        let turnUsage = null as ReturnType<typeof parseUsageFromSse>;
        // Set only by the turn's terminal frames — a stream that ends without
        // them was cut off and must be followed, not finalized.
        let sawTerminal = false;
        let wasAborted = false;

        const processLine = (rawLine: string) => {
          const line = rawLine.trim();
          if (!line.startsWith("data:")) return;
          const jsonStr = line.slice(5).trim();
          if (jsonStr === "") return;
          if (jsonStr === "[DONE]") {
            sawTerminal = true;
            return;
          }

          try {
            const parsed = JSON.parse(jsonStr) as {
              type?: string;
              content?: string;
              fullText?: string;
              toolName?: string;
              toolCallId?: string;
              toolInput?: unknown;
              inputTokens?: number;
              outputTokens?: number;
              contextTokens?: number;
              model?: string;
              result?: unknown;
              error?: string;
            };

            if (parsed.error) {
              throw new ChatStreamError(parsed.error);
            }
            if (parsed.type === "thinking_chunk" && parsed.content) {
              thinkingRaw += parsed.content;
              setStreamingBlocks(turn.onThinkingChunk(parsed.content));
            } else if (parsed.type === "chunk" && parsed.content) {
              setStreamingBlocks(turn.onTextChunk(parsed.content));
              fullText += parsed.content;
            } else if (parsed.type === "aborted") {
              wasAborted = true;
            } else if (parsed.type === "complete" && parsed.fullText) {
              fullText = parsed.fullText;
              sawTerminal = true;
              setStreamingBlocks(turn.onStreamComplete(parsed.fullText));
            } else if (parsed.type === "usage_progress") {
              const input = parsed.inputTokens ?? 0;
              const output = parsed.outputTokens ?? 0;
              if (input + output > 0) setLiveTokens(input + output);
            } else if (parsed.type === "usage") {
              const usage = parseUsageFromSse(parsed);
              if (usage) {
                turnUsage = usage;
                setContextTokens(usage.contextTokens);
              }
            } else if (parsed.type === "tool_start" && parsed.toolName) {
              const toolCallId =
                parsed.toolCallId ?? `${parsed.toolName}-${toolEvents.length}`;
              const event: StreamingToolEvent = {
                toolName: parsed.toolName,
                toolCallId,
                input: parsed.toolInput ?? null,
                result: null,
                status: "running",
                startedAt: new Date().toISOString(),
                endedAt: "",
                // Where the call sits in the turn — see splitTurnRounds.
                textOffset: fullText.length,
                thinkingOffset: thinkingRaw.length,
                thinkingMs: turn.thinkingMsSoFar(),
              };
              toolEvents.push(event);
              setStreamingBlocks(turn.onToolStart(event));
            } else if (parsed.type === "tool_end" && parsed.toolName) {
              const toolCallId =
                parsed.toolCallId ??
                toolEvents.find(
                  (e) => e.toolName === parsed.toolName && e.status === "running",
                )?.toolCallId;
              const event = toolCallId
                ? toolEvents.find((e) => e.toolCallId === toolCallId)
                : toolEvents.find(
                    (e) =>
                      e.toolName === parsed.toolName && e.status === "running",
                  );
              if (event) {
                event.result = parsed.result ?? null;
                event.status = "completed";
                event.endedAt = new Date().toISOString();
                setStreamingBlocks(
                  turn.onToolEnd(event.toolCallId, {
                    result: event.result,
                    status: event.status,
                    endedAt: event.endedAt,
                  }),
                );
              }
              invalidateForTool(parsed.toolName, parsed.result ?? null);
            }
          } catch (error) {
            if (error instanceof ChatStreamError) throw error;
            if (error instanceof Error && error.message !== "Unexpected end of JSON input") {
              console.error("Error parsing SSE:", error);
            }
          }
        };

        // Buffer across reads: an SSE `data:` line is regularly split over two
        // network reads — parsing each read in isolation silently drops it.
        let buffer = "";
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";
            for (const line of lines) processLine(line);
          }
          if (buffer.trim() !== "") processLine(buffer);
        } finally {
          reader.releaseLock();
        }

        if (!sawTerminal) {
          // Cut off mid-turn — generation continues server-side; hand off to
          // the resume loop, which follows the row to completion.
          throw new ChatStreamError("Stream ended before the turn completed");
        }

        const { totalThinkingMs } = turn.finalize();

        const assistantMessage: ChatMessage = {
          id: `local-${Date.now() + 1}`,
          workflowRunId: "",
          role: "assistant",
          content: fullText,
          metadata: buildAssistantMessageMetadata(
            thinkingRaw.trim() ? { text: thinkingRaw, durationMs: totalThinkingMs } : null,
            turnUsage,
          ),
          toolEvents:
            toolEvents.length > 0
              ? toolEvents.map((e) => ({
                  toolName: e.toolName,
                  toolCallId: e.toolCallId,
                  input: e.input,
                  result: e.result,
                  startedAt: e.startedAt,
                  endedAt: e.endedAt,
                  textOffset: e.textOffset,
                  thinkingOffset: e.thinkingOffset,
                  thinkingMs: e.thinkingMs,
                }))
              : undefined,
          createdAt: replyCreatedAt,
          ...(wasAborted ? { status: "aborted" as const } : {}),
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setStreamingBlocks([]);
        onSessionsChanged?.();
      } catch (error) {
        console.error("Workshop chat error:", error);
        const body =
          !streamStarted && quickCreates.length > 0 && isAxiosError(error) && error.response?.status === 409
            ? await refusedBody(error)
            : null;
        if (body?.code === QUICK_CREATE_UNAVAILABLE) {
          // A pick went while it sat in the draft: nothing was stored.
          setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
          opts?.onQuickCreateRefused?.(
            typeof body.kind === "string" && typeof body.key === "string"
              ? pickId({ kind: body.kind === "agent" ? "agent" : "skill", key: body.key })
              : "*",
          );
        } else if (!streamStarted && isAxiosError(error) && error.response?.status === 409) {
          // This tab lost track of a reply the server is still writing. The new
          // message was not saved: take it back out, and follow that reply.
          setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
          queue.add({
            title: "Your message wasn't sent",
            description: "A reply is still being written. Wait for it or stop it, then send again.",
            variant: "warning",
          });
          handedOffToRecovery = true;
          setStreamRecoveryKey((k) => k + 1);
        } else if (streamStarted) {
          // A turn row exists and generation continues server-side — resume.
          handedOffToRecovery = true;
          setStreamRecoveryKey((k) => k + 1);
        } else if (isUnreachable(error)) {
          // Nothing answered. The server stores the message before it starts
          // the reply, so read the thread before calling it unsent.
          const found =
            activeSessionId && userMessageId
              ? await sendLanded(activeSessionId, trimmed, userRowsBefore)
              : { landed: false, replying: false };
          if (found?.landed || found?.replying) {
            // It is being answered (or was): follow the reply in place.
            handedOffToRecovery = true;
            setStreamRecoveryKey((k) => k + 1);
          }
          if (!found?.landed) {
            // Not in the thread (or the thread could not be read): the message
            // waits above the composer rather than sitting in the thread as if
            // sent. A reply already running is someone else's turn.
            setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
            holdUnsent({
              text: trimmed,
              sessionId: activeSessionId,
              checked: found !== null,
              userRowsBefore,
            });
          }
        } else {
          setStreamError(
            getAiRequestErrorMessage(
              error,
              "Creo stopped before it finished. Nothing was lost.",
            ),
          );
        }
      } finally {
        if (liveSendRef.current === send) liveSendRef.current = null;
        if (!handedOffToRecovery) {
          // On handoff the turn is still live: keep the streaming UI up and the
          // composer locked until the resume loop settles it. Every resume path
          // ends in settle(), which resets both.
          setIsStreaming(false);
          setStreamingBlocks([]);
          setLiveTokens(null);
        }
        refreshAiBudget();
      }
    },
    [
      projectId,
      sessionId,
      isStreaming,
      onSessionCreated,
      onSessionsChanged,
      invalidateForTool,
      selectedModel?.id,
      selectedModel?.reasoningOptions,
      selectedReasoningEffort,
      refreshAiBudget,
      sendLanded,
      holdUnsent,
    ],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLoadedSessionKey(null);
    setStreamingBlocks([]);
    setContextTokens(null);
    setStreamError(null);
    setHasOlder(false);
    setUnloadedUserRows(0);
  }, [setUnloadedUserRows]);

  // Keyed on the session, not the project: opening another thread loads too.
  const isLoadingInitialMessages =
    currentSessionKey !== null &&
    loadedSessionKey !== currentSessionKey &&
    !isStreaming &&
    messages.length === 0;
  // True only once this session's transcript has been read. A live reply must
  // not count: it starts before the history arrives.
  const messagesLoaded =
    currentSessionKey !== null && loadedSessionKey === currentSessionKey;

  return {
    messages,
    isLoadingMessages: isLoadingInitialMessages,
    messagesLoaded,
    streamingBlocks,
    isStreaming,
    liveTokens,
    selectedModel,
    setSelectedModel,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    seedSelection,
    seededKey,
    markSeeded,
    contextTokens,
    streamError,
    unsentByThread,
    holdUnsent,
    takeUnsent,
    unsentRemainder,
    sendMessage,
    joinSend,
    stopStreaming,
    isStopping,
    clearMessages,
    paging: { hasOlder, isLoadingOlder, loadOlder, trimOlder, userRowOffset },
  };
}
